import React, { useState, useEffect, useRef } from 'react';
import { PostRequest, GetRequest } from 'components/common/index.js';
import { ContentPopup } from 'components/ContentPopup.jsx';
import AttributePropertyTags from 'components/quote_builder/attributePropertyTags.jsx';
import AddMachineFlow from 'components/frank/AddMachineFlow.jsx';
import QuoteBuilderForm from 'components/quote_builder/QuoteBuilderForm.jsx';
import AddedToWidgetCard from 'components/quote_builder_widget/shared/AddedToWidgetCard';
import AddToWidgetTable from 'components/quote_builder_widget/shared/AddToWidgetTable';
import AddPopupButtons from 'components/quote_builder_widget/shared/AddPopupButtons';
import styles from 'components/quote_builder_widget/settings/_style.module.scss';

const translate = (key) => t(`quote_builder_widget.${key}`);

// A machine reference field set to "a new machine" (UserUnitPriceAttributeField's VALUE_NEW) shows the new machine's
// name input.
const NEW_MACHINE = 'new-attribute';

// The machines of one kind (machineKey: printing_machine, die_cutting_machine, hot_stamping_machine) the widget prices on. The owner adds a
// machine as they add a material. Every machine that can be added (can_be_added) is offered, also before the widget
// materials it is used for; an added machine none of the widget's materials use says so (supported_by_widget_materials),
// and when none of the owner's machines can be added the list endpoint's no_machines_can_be_added says which the widget
// uses (a hot stamping machine always can be). Each added machine is an AddedToWidgetCard holding the property pills the
// Manufacturing Cost Calculator settings list shows (SettingsUserUnitPriceAttributeList), read through that machine's
// settings configuration; Edit opens the machine's own form, as that list does. labels names the quote_builder_widget
// translation key of each text.
//
// With labels.createNew the owner can also create a machine here, and it is added to the widget at once -- as a
// material created in the widget is. As the settings page does, a machine whose settings tab has a library (a printing
// machine) is created as /mcc adds one (AddMachineFlow: pick it from the library or enter it manually, then its price);
// any other (a hot stamping machine, which is only its name) inside its price form. allowedTypesField names the list
// endpoint's field holding the types the widget can use, the only ones the library offers; warningsField the field
// holding the warnings it shows above the added machines.
const WidgetMachinesSection = ({
  machineKey, listUrl, listField, allowedTypesField, warningsField, labels, onMachinesChange, onAddedCountChange
}) => {
  const [machines, setMachines] = useState([]);
  const [allowedTypes, setAllowedTypes] = useState([]);
  const [noMachinesCanBeAdded, setNoMachinesCanBeAdded] = useState('');
  const [warnings, setWarnings] = useState([]);
  const [settingsConfiguration, setSettingsConfiguration] = useState(null);
  const [machineFormConfiguration, setMachineFormConfiguration] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [updatingId, setUpdatingId] = useState(null);
  const [selectingAll, setSelectingAll] = useState(false);
  const [showAddPopup, setShowAddPopup] = useState(false);
  const [showCreatePopup, setShowCreatePopup] = useState(false);
  const [editingMachineId, setEditingMachineId] = useState(null);
  const addPopupRef = useRef();
  const createPopupRef = useRef();
  const editPopupRef = useRef();

  const fetchMachines = () => {
    GetRequest({
      url: listUrl,
      callback: (data) => {
        setMachines(data[listField]);
        setNoMachinesCanBeAdded(data.no_machines_can_be_added);
        if (allowedTypesField) {
          setAllowedTypes(data[allowedTypesField]);
        }
        if (warningsField) {
          setWarnings(data[warningsField]);
        }
        setLoading(false);
      },
      // Any failure -- an error status, an expired session, a dropped connection -- ends the loading state.
      onError: () => {
        setLoading(false);
        setErrorMessage(translate(labels.failedToLoad));
      }
    });
  };

  const fetchConfigurations = () => {
    GetRequest({
      url: `/quote-builder/config/settings/user_unit_prices/${machineKey}`,
      useCache: true,
      callback: (data) => { setSettingsConfiguration(data); },
      onError: () => { setErrorMessage(translate(labels.failedToLoad)); }
    });
    GetRequest({
      url: `/quote-builder/user_unit_price_attributes/config/${machineKey}/${machineKey}`,
      callback: (data) => { setMachineFormConfiguration(data); }
    });
  };

  useEffect(() => {
    fetchMachines();
    fetchConfigurations();
  }, []);

  const tabConfiguration = (settingsConfiguration?.tabs || []).find((tab) => tab.form_type === 'UserUnitPriceAttribute');

  const closeAddPopup = () => {
    setShowAddPopup(false);
    if (addPopupRef.current) {
      addPopupRef.current.style.display = 'none';
    }
  };

  const openCreatePopup = () => {
    closeAddPopup();
    setErrorMessage('');
    setShowCreatePopup(true);
  };

  // The saved price names its machine -- the new one, or one the owner chose instead -- which is added to the widget.
  const addPricedMachineToWidget = (priceId) => {
    closeCreatePopup();
    GetRequest({
      url: `/quote-builder/user_unit_prices/${priceId}`,
      callback: (price) => { updateWidgetStatus({ id: price[`user_unit_price_attribute.${machineKey}`] }, true); },
      onError: () => { setErrorMessage(translate(labels.failedToUpdate)); }
    });
  };

  const refreshMachines = () => {
    fetchMachines();
    if (onMachinesChange) {
      onMachinesChange();
    }
  };

  const closeCreatePopup = () => {
    setShowCreatePopup(false);
    setErrorMessage('');
    if (createPopupRef.current) {
      createPopupRef.current.style.display = 'none';
    }
  };

  const closeEditPopup = () => {
    setEditingMachineId(null);
    if (editPopupRef.current) {
      editPopupRef.current.style.display = 'none';
    }
  };

  const afterMachineEdited = () => {
    closeEditPopup();
    refreshMachines();
  };

  const updateWidgetStatus = (machine, widgetEnabled) => {
    setUpdatingId(machine.id);
    setErrorMessage('');
    PostRequest({
      url: `/quote-builder/user_unit_price_attributes/${machine.id}/update-widget-status`,
      data: { widget_enabled: widgetEnabled ? 'true' : 'false' },
      callback: () => {
        setUpdatingId(null);
        closeAddPopup();
        fetchMachines();
        if (onMachinesChange) {
          onMachinesChange();
        }
      },
      400: (data) => {
        setUpdatingId(null);
        setErrorMessage(data?.error || translate(labels.failedToUpdate));
      },
      404: () => {
        setUpdatingId(null);
        setErrorMessage(translate(labels.failedToUpdate));
      },
      500: () => {
        setUpdatingId(null);
        setErrorMessage(translate('server_error'));
      }
    }).catch(() => {
      // PostRequest also rejects after running a status handler, whose message stays; a failure no handler covers (a
      // dropped connection) gets one.
      setUpdatingId(null);
      setErrorMessage((currentMessage) => currentMessage || translate(labels.failedToUpdate));
    });
  };

  // The server selects every machine of this kind the popup lists: the owner's, not yet selected, that can be.
  const selectAllMachines = () => {
    setSelectingAll(true);
    setErrorMessage('');
    PostRequest({
      url: '/quote-builder/user_unit_price_attributes/select-all-for-widget',
      data: { key: machineKey },
      callback: () => {
        setSelectingAll(false);
        closeAddPopup();
        fetchMachines();
        if (onMachinesChange) {
          onMachinesChange();
        }
      },
      400: (data) => {
        setSelectingAll(false);
        setErrorMessage(data?.error || translate(labels.failedToUpdate));
      },
      500: () => {
        setSelectingAll(false);
        setErrorMessage(translate('server_error'));
      }
    }).catch(() => {
      setSelectingAll(false);
      setErrorMessage((currentMessage) => currentMessage || translate(labels.failedToUpdate));
    });
  };

  const renderErrorMessage = () => {
    if (!errorMessage) {
      return null;
    }
    return (
      <div style={{ color: '#dc3545', marginBottom: '10px', padding: '8px', backgroundColor: '#f8d7da', borderRadius: '4px' }}>
        {errorMessage}
      </div>
    );
  };

  const addedMachines = machines.filter((machine) => machine.widget_enabled);
  const addableMachines = machines.filter((machine) => machine.can_be_added && !machine.widget_enabled);
  // One request at a time: a row's Select and Select All would each close the popup and refetch under the other.
  const busy = updatingId !== null || selectingAll;

  useEffect(() => {
    if (!loading && onAddedCountChange) {
      onAddedCountChange(addedMachines.length);
    }
  }, [loading, addedMachines.length]);

  const canCreateMachine = Boolean(labels.createNew && tabConfiguration);

  const noAddableMachinesMessage = () => {
    if (machines.length === 0) { return translate(labels.noMachines); }
    if (!machines.some((machine) => machine.can_be_added)) { return noMachinesCanBeAdded; }
    return translate(labels.allAdded);
  };

  const machineDetails = (machine) => (
    <>
      {machine.widget_enabled && !machine.supported_by_widget_materials && (
        <div data-testid="widget-machine-not-used" style={{ color: '#dc3545', fontSize: '12px' }}>
          {translate(labels.notUsed)}
        </div>
      )}
      <AttributePropertyTags
        properties={machine.properties}
        listingProperties={tabConfiguration?.listing_properties}
        subFormAttributes={machineFormConfiguration?.create_sub_form?.attributes}
        sectionAttributes={settingsConfiguration?.attributes}
      />
    </>
  );

  const renderAddedMachines = () => {
    if (addedMachines.length === 0) {
      return (
        <div style={{ color: '#666', fontStyle: 'italic', marginBottom: '10px' }}>
          {translate(labels.noneAdded)}
        </div>
      );
    }

    return (
      <div style={{ marginBottom: '10px' }}>
        {addedMachines.map((machine) => (
          <AddedToWidgetCard
            key={machine.id}
            testId="widget-machine"
            title={machine.name}
            onEdit={machineFormConfiguration?.create_sub_form ? () => setEditingMachineId(machine.id) : null}
            editDisabled={!machine.can_be_edited}
            onRemove={() => updateWidgetStatus(machine, false)}
            removing={updatingId === machine.id}
          >
            {machineDetails(machine)}
          </AddedToWidgetCard>
        ))}
      </div>
    );
  };

  const renderEditPopupContent = () => (
    <div style={{ padding: '20px', minWidth: '500px' }}>
      <QuoteBuilderForm
        config={machineFormConfiguration.create_sub_form}
        showingState="edit"
        editStateId={editingMachineId}
        editedKlass="UserUnitPriceAttribute"
        linkToSettings={false}
        afterSave={afterMachineEdited}
        afterCancel={closeEditPopup}
      />
    </div>
  );

  const renderAddPopupContent = () => (
    <div style={{ padding: '20px', minWidth: '500px' }}>
      <h4>{translate(labels.addTitle)}</h4>
      {renderErrorMessage()}
      {addableMachines.length === 0 ? (
        <p>{noAddableMachinesMessage()}</p>
      ) : (
        <AddToWidgetTable
          title={translate(labels.select)}
          testId="widget-machine"
          disabled={busy}
          rows={addableMachines.map((machine) => ({
            id: machine.id,
            title: machine.name,
            details: machineDetails(machine),
            onAdd: () => updateWidgetStatus(machine, true),
          }))}
        />
      )}
      <AddPopupButtons
        onSelectAll={addableMachines.length > 0 ? selectAllMachines : null}
        selectingAll={selectingAll}
        disabled={busy}
        createNewLabel={translate(labels.createNew)}
        onCreateNew={canCreateMachine ? openCreatePopup : null}
      />
    </div>
  );

  // In AddMachineFlow the new machine is added to the widget as soon as it exists, before its price. A machine entered
  // manually with a printing type the widget cannot use is refused, and the refusal shows above the flow.
  const renderCreatePopupContent = () => (
    <div style={{ padding: '20px', minWidth: '500px' }}>
      {renderErrorMessage()}
      {tabConfiguration.library_enabled ? (
        <AddMachineFlow
          machineKey={machineKey}
          tabConfig={tabConfiguration}
          allowedTypes={allowedTypes}
          onCopied={(newAttributeId) => updateWidgetStatus({ id: newAttributeId }, true)}
          onPriced={refreshMachines}
          onClose={closeCreatePopup}
        />
      ) : (
        <QuoteBuilderForm
          config={settingsConfiguration}
          showingState="new"
          context={{ [machineKey]: NEW_MACHINE }}
          afterSave={addPricedMachineToWidget}
          afterCancel={closeCreatePopup}
        />
      )}
    </div>
  );

  if (loading) {
    return (
      <div style={{ padding: '10px', color: '#666' }}>
        {translate('loading')}
      </div>
    );
  }

  return (
    <div>
      {!showAddPopup && !showCreatePopup && renderErrorMessage()}
      {warnings.map((warning) => (
        <div key={warning} data-testid="widget-machines-warning" className={styles.machinesWarning}>
          <i className="fa fa-exclamation-triangle" aria-hidden="true"></i>{' '}{warning}
        </div>
      ))}
      {renderAddedMachines()}
      <button type="button" className="btn simple-btn" onClick={() => setShowAddPopup(true)}>
        <i className="fa fa-plus-square-o" aria-hidden="true"></i>{' '}
        {translate(labels.add)}
      </button>
      {showAddPopup && (
        <ContentPopup
          myRef={addPopupRef}
          autoOpen={true}
          content={renderAddPopupContent()}
          cancelButton={false}
          beforeClose={() => {
            setShowAddPopup(false);
            setErrorMessage('');
          }}
        />
      )}
      {showCreatePopup && (
        <ContentPopup
          myRef={createPopupRef}
          autoOpen={true}
          content={renderCreatePopupContent()}
          cancelButton={false}
          beforeClose={closeCreatePopup}
        />
      )}
      {editingMachineId && (
        <ContentPopup
          myRef={editPopupRef}
          autoOpen={true}
          content={renderEditPopupContent()}
          cancelButton={false}
          beforeClose={closeEditPopup}
        />
      )}
    </div>
  );
};

export default WidgetMachinesSection;
